import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Input, Modal, Row, Typography } from 'antd';
import { FctDato, fmtNumero } from '../../fct/react';
import {
  borrarInforme, leerImpactoBorrado, mensajeDeError, type ImpactoBorrado, type InformeCatalogo,
} from '../../api/reportesApi';

interface Props {
  informe: InformeCatalogo;
  onCerrar: () => void;
  onBorrado: () => void;
}

/**
 * El aviso antes de borrar un informe (#411): dice en rojo que no se puede deshacer, cuenta lo que se pierde y recuerda que
 * desactivarlo es la alternativa. El botón solo se activa al escribir el nombre exacto y con los recuentos a la vista. La API
 * comprueba otra vez lo escrito. Una fila sin nombre se confirma con su número.
 */
const BorrarInforme: React.FC<Props> = ({ informe, onCerrar, onBorrado }) => {
  const esperado = informe.nombre?.trim() || String(informe.id);
  const [impacto, setImpacto] = useState<ImpactoBorrado | null>(null);
  const [escrito, setEscrito] = useState('');
  const [borrando, setBorrando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    leerImpactoBorrado(informe.id)
      .then((leido) => { if (vivo) setImpacto(leido); })
      .catch((e) => { if (vivo) setError(mensajeDeError(e, 'No se ha podido contar lo que se borraría. Inténtalo de nuevo.')); });
    return () => { vivo = false; };
  }, [informe.id]);

  const borrar = async () => {
    setBorrando(true);
    setError(null);
    try {
      await borrarInforme(informe.id, escrito);
      onBorrado();
    } catch (e) {
      setError(mensajeDeError(e, 'No se ha podido borrar el informe. Inténtalo de nuevo en unos minutos.'));
      setBorrando(false);
    }
  };

  return (
    <Modal
      open
      onCancel={onCerrar}
      title="Eliminar informe"
      width={560}
      footer={(
        <>
          <Button onClick={onCerrar}>Cancelar</Button>
          <Button type="primary" danger disabled={!impacto || escrito !== esperado} loading={borrando} onClick={borrar}>
            Eliminar para siempre
          </Button>
        </>
      )}
    >
      <Alert
        type="error"
        showIcon
        message={<Typography.Text type="danger" strong>Esto no se puede deshacer.</Typography.Text>}
        description="Si solo quieres que deje de abrirse, puedes desactivarlo en el formulario, sin perder nada."
        style={{ marginBottom: 12 }}
      />
      <Row gutter={16} style={{ marginBottom: 12 }}>
        <Col xs={24} sm={8}><FctDato etiqueta="Informe" valor={informe.nombre ?? `Sin nombre (n.º ${informe.id})`} /></Col>
        <Col xs={12} sm={8}><FctDato etiqueta="Accesos nominales que se borran" valor={impacto ? fmtNumero(impacto.accesosNominales) : null} mono /></Col>
        <Col xs={12} sm={8}><FctDato etiqueta="Accesos en el log" valor={impacto ? fmtNumero(impacto.accesosEnLog) : null} mono /></Col>
      </Row>
      <p>El log se conserva: sus filas quedan sin el número del informe, y su GUID sigue diciendo de cuál eran.</p>
      <label htmlFor="reportes-borrar-confirmacion">Escribe <strong>{esperado}</strong> para confirmar</label>
      <Input
        id="reportes-borrar-confirmacion"
        autoComplete="off"
        value={escrito}
        onChange={(e) => setEscrito(e.target.value)}
        style={{ marginTop: 4 }}
      />
      {error && <Alert type="error" showIcon message={error} style={{ marginTop: 12 }} />}
    </Modal>
  );
};

export default BorrarInforme;
